import { createSelector } from 'reselect';
import { getFilledCollection } from 'Reducers/componentHelpers';
import { urls as locationsUrls } from 'Reducers/locations';
import * as urlHelper from 'Helpers/url';
import * as objectsHelper from 'Helpers/objects';

import { getCategoryElement } from 'Selectors/categories';
import { getLocationElements, selectedLocationSelector } from 'Selectors/location';
import { itemsFacetMetadataSelector } from 'Selectors/items';
import { projectFacetFilterSelector, buildProjectURL } from 'Selectors/projects';
import { getType } from 'Reducers/helpers/typeModifier';
import { buildURL } from 'Helpers/url';

const descendant = {
    COUNTRY: 'STATE',
    STATE: 'CITY',
    CITY: 'NEIGHBOURHOOD',
    NEIGHBOURHOOD: 'NEIGHBOURHOOD'
};

const descendantWithoutState = {
    COUNTRY: 'CITY',
    CITY: 'NEIGHBOURHOOD',
    NEIGHBOURHOOD: 'NEIGHBOURHOOD'
};

const getDecendantByType = (type, isCountryWithoutState) => (isCountryWithoutState ? descendantWithoutState[type] : descendant[type]);

const getPopularParams = (location = { type: 'COUNTRY' }, lang, isCountryWithoutState) => {
    const descendants = isCountryWithoutState ? descendantWithoutState : descendant;
    const params = {
        limit: 5,
        type: location && location.type ? getDecendantByType(location.type, isCountryWithoutState) : descendants.COUNTRY
    };

    if (location && location.type && location.type !== 'COUNTRY') {
        params.parent = location.type === 'NEIGHBOURHOOD' ? location.parentId : location.id;
    }

    if (lang) {
        params.lang = lang;
    }

    return params;
};

export const getPopularUrl = params => {
    const url = locationsUrls.getPopulars;

    if (params && !objectsHelper.isEmpty(params)) {
        return `${url}?${urlHelper.paramsToQueryString(params)}`;
    }
    return url;
};

export const popularLocationsParamsSelectorWithParam = createSelector(
    (_, selectedLocation) => selectedLocation,
    state => state.api && state.api.queryParam && state.api.queryParam.enableMultiLang && state.api.queryParam.lang,
    (_, __, isCountryWithoutState) => isCountryWithoutState,
    (selectedLocation, lang, isCountryWithoutState) => {
        if (selectedLocation && !objectsHelper.isEmpty(selectedLocation)) {
            return getPopularParams(selectedLocation, lang, isCountryWithoutState);
        }
        return getPopularParams(lang, null, isCountryWithoutState);
    }
);

export const popularLocationsParamsSelector = createSelector(
    selectedLocationSelector,
    state => state.api && state.api.queryParam && state.api.queryParam.enableMultiLang && state.api.queryParam.lang,
    (_, isCountryWithoutState) => isCountryWithoutState,
    (selectedLocation, lang, isCountryWithoutState) => {
        if (selectedLocation && !objectsHelper.isEmpty(selectedLocation)) {
            return getPopularParams(selectedLocation, lang, isCountryWithoutState);
        }
        return getPopularParams(lang, null, isCountryWithoutState);
    }
);

export const isFetchingPopularSelector = createSelector(
    ({ locations }) => locations.isFetching,
    popularLocationsParamsSelector,
    (isFetching, params) => isFetching[getType('LOCATIONS', getPopularUrl(params))]
);

export const popularLocationsSelector = createSelector(
    ({ locations }) => locations,
    popularLocationsParamsSelector,
    (locations, params) => getFilledCollection('LOCATIONS', locations, locationsUrls.getPopulars, params)
);

const makeLink = ({ category, location, props, search, withCount = false }) => ({
    id: location.id,
    count: location.count,
    showCount: withCount,
    title: location.name,
    to: props.type === 'projects' ? buildProjectURL({ location, constructionStatus: props.constructionStatus, base: props.type })
        : buildURL({
            location: location.location_type === 'COUNTRY' ? {} : location,
            category,
            params: props.params,
            search
        })
});

const maxIter = 3;

const popularLocationsRecur = (props, search, category, list, [id, ...rest], depth = 0) => {
    if (!list || list.length === 0) {
        return [];
    }
    if (depth === maxIter) {
        return list
            .filter(a => a.count > 0)
            .sort((a, b) => b.count - a.count)
            .map(location => ({
                ...makeLink({ category, location, props, search, withCount: true }),
                selected: location.id === id,
                list: []
            }));
    }

    const location = list.find(location => location.id === id);

    if (!location) {
        return [];
    }

    if (rest.length === 0) {
        return [{
            ...makeLink({
                category,
                location,
                props,
                search
            }),
            selected: true,
            list: popularLocationsRecur(props, search, category, location.children, [], maxIter)
        }];
    }

    return [{
        ...makeLink({
            category,
            location,
            props,
            search
        }),
        selected: false,
        list: popularLocationsRecur(props, search, category, location.children, rest, depth + 1)
    }];
};
const getFilterData = (state, props) => {
    switch (props.type) {
        case 'projects':
            return projectFacetFilterSelector(state, props);
        case 'landingPage':
            return state.filtersAbundance && state.filtersAbundance.data;
        default :
            return itemsFacetMetadataSelector(state, props);
    }
};

const getMetadataFilter = createSelector(
    getFilterData,
    (metadata = {}) => {
        const filter = metadata.filters
            && Array.isArray(metadata.filters)
            && metadata.filters.find(({ id }) => id === 'location');

        return (filter && filter.values) ? filter.values : [];
    }
);
const getParentsRecur = (data, id, parents = []) => {
    const item = data[id];

    if (!item || !item.parentId) {
        return parents.reverse();
    }

    return getParentsRecur(data, item.parentId, [...parents, item.parentId.toString()]);
};

const getLocationId = createSelector(
    selectedLocationSelector,
    getMetadataFilter,
    (location, list) => {
        if (location && location.id) {
            return location.id.toString();
        }

        return list[0] && list[0].id || '';
    }
);

export const getCurrentListingLocation = createSelector(
    getLocationId,
    getLocationElements,
    (id, locations) => locations[id]
);

export const getParents = createSelector(
    getLocationElements,
    getLocationId,
    (data, id) => getParentsRecur(data, id, [id])
);

export const getPopularLocationsLeftBar = createSelector(
    (_, props) => props,
    (_, { search = '' }) => search,
    getCategoryElement,
    getMetadataFilter,
    getParents,
    popularLocationsRecur
);

const locations = createSelector(
    ({ locations }) => locations,
    getCurrentListingLocation,
    (locations, currentLocation) => getFilledCollection('LOCATIONS', locations, locationsUrls.getLocations, { parent: currentLocation.id })
);

export const getCurrentListingLocations = createSelector(
    (_, props) => props,
    (_, { search = '' }) => search,
    getCategoryElement,
    locations,
    (props, search, category, locations) => locations
        .map(location => makeLink({ category, location, props, search, withCount: false }))
);
